import _ from 'underscore';
import React, { useEffect, useMemo } from 'react';
import { Activity } from 'botframework-directlinejs';
import { EVENT_NAME_ENGAGE_TROUBLESHOOTER, EVENT_NAME_LIVE_AGENT_CHAT, useAdsCopilotPilotMap } from '@bingads-webui/ads-copilot-ui';
import { type ActivityRenderer } from '@bingads-webui-react/msa-copilot-common';
import {
  LiveAgentSupportClosed,
  LiveAgentUnsupported,
} from '../live-agent-cards';
import { LiveAgentTransferCard } from '../live-agent-transfer-card';
import {
  LIVE_AGENT_LIMITED_SUPPORT_LOCALES,
  isLiveChatSupportOpen,
  isLocaleSupported,
  isEnglishOnlyChatSupportedLocale,
  shouldSkipLiveChatSupportOpenCheck,
} from '../../helpers/live-agent-locale-utils';
import {
  LiveAgentTransferMessages,
  LiveAgentRedBucketReasonEnum,
} from '../../helpers/live-agent-helper';

export const LiveAgentTransferCardRenderer: ActivityRenderer = (props) => {
  const {
    activity,
    logger,
    onFeedbackButtonClick,
    params,
    liveAgentActivityProps: {
      isAIDegraded,
      locale,
      onLiveAgentTokensReceive,
      onLiveAgentTransferClick,
      isCaptchaEnabled,
    },
    isInlineLiveAgentButtonEnabled,
    disabled,
    renderConnectAction,
    cardTextOverride,
  } = props;

  const {
    isIntelligentRoutingEnabled,
    isRedirectLiveAgentEnabled,
    isRedBucketEnabled,
  } = useAdsCopilotPilotMap();

  const isEnglishOnlyChatSupportAvailable = useMemo(() => isEnglishOnlyChatSupportedLocale(locale), [locale]);
  const isLiveChatSupportOpenCheckSkipped = useMemo(() => shouldSkipLiveChatSupportOpenCheck(locale), [locale]);
  const liveAgentTransferType = useMemo(() => {
    if (isRedBucketEnabled) {
      const category = (_.get(activity, ['liveAgentData', 'ConversationCategory']))?.toLowerCase();
      if (category !== 'green') {
        const redBucketReason = (_.get(activity, ['liveAgentData', 'reason']) || _.get(activity, ['liveAgentData', 'Reason']))?.toLowerCase();
        const conversationId = _.get(activity, ['conversation', 'id']);
        logger?.trace(`Copilot - Live Agent: Red Bucket Details - ${redBucketReason} - ConversationId: ${conversationId}`);
        if (redBucketReason && LiveAgentRedBucketReasonEnum[redBucketReason]) {
          return redBucketReason;
        } else if (redBucketReason && !LiveAgentRedBucketReasonEnum[redBucketReason]) {
          logger?.trace(`Copilot - Live Agent: Red Bucket Details - ${redBucketReason} - Reason unmapped - ConversationId: ${conversationId}`);
        }
      }
    }
    return _.get(activity, 'valueType');
  }, [activity, isRedBucketEnabled, logger]);

  const lowerLocale = locale ? locale.toLocaleLowerCase() : undefined;

  // disable the card button if this activity is no longer the newest message
  const isCardButtonDisabled = useMemo<boolean>(() => {
    if (params.currentActivities) {
      for (let i = params.currentActivities.length - 1; i >= 0; i -= 1) {
        const a = params.currentActivities[i];
        if (a.id === activity.id) {
          // ConnectToAgent activity is the newest
          // verify its not a replayed activity
          return disabled;
        }
        if (a.type === 'message') {
          return true;
        }
      }
    }
    return true;
  }, [activity.id, params.currentActivities]);

  const liveAgentCardText = useMemo(() => {
    if (cardTextOverride) {
      return cardTextOverride;
    }
    if (liveAgentTransferType) {
      if (liveAgentTransferType in LiveAgentTransferMessages) {
        return LiveAgentTransferMessages[liveAgentTransferType];
      }
      logger?.trace(`Copilot - Live Agent: Transfer card - Unknown transfer type: ${liveAgentTransferType}`);
    }
    // Return a default message if the transfer type is unknown;
    return LiveAgentTransferMessages.ConnectToAgentNoAIResponse;
  }, [liveAgentTransferType, cardTextOverride]);

  useEffect(() => {
    if (isLiveChatSupportOpenCheckSkipped) {
      logger?.trace(`Copilot - Live Agent: Support hours check skipped. Locale: ${locale}`);
    }
    logger?.trace(`Copilot - Live Agent: Transfer card - Locale: ${locale}`);
  }, [locale, isLiveChatSupportOpenCheckSkipped]);

  if (!lowerLocale || (!isLocaleSupported(lowerLocale) && !isEnglishOnlyChatSupportAvailable)) {
    logger?.trace(`Copilot - Live Agent: Unsupported card - Locale: ${lowerLocale}`);
    return <LiveAgentUnsupported onFeedbackButtonClick={onFeedbackButtonClick} />;
  }

  if (!isLiveChatSupportOpenCheckSkipped && !isLiveChatSupportOpen(lowerLocale) && !isEnglishOnlyChatSupportAvailable) {
    logger?.trace(`Copilot - Live Agent: Support closed card - Locale: ${lowerLocale}`);
    // 24/7 locales are absent from this map and close only for a holiday.
    const { customPlaceholder, timezoneNote, weekdayTime } = LIVE_AGENT_LIMITED_SUPPORT_LOCALES[lowerLocale] ?? {};
    return (
      <LiveAgentSupportClosed
        customPlaceholder={customPlaceholder}
        onFeedbackButtonClick={onFeedbackButtonClick}
        weekdayTime={weekdayTime}
        timezoneNote={timezoneNote}
      />
    );
  }

  if (activity?.channelData?.JwtTokens) {
    onLiveAgentTokensReceive(activity.channelData.JwtTokens);
  }

  if (isInlineLiveAgentButtonEnabled
    && !renderConnectAction
    && ((liveAgentTransferType === 'ConnectToAgentNoAIResponseRedScore' || liveAgentTransferType === 'ConnectToAgentWithAIResponseYellowScore')
        || (isRedBucketEnabled && LiveAgentRedBucketReasonEnum[liveAgentTransferType]))) {
    return null;
  }

  return (
    <LiveAgentTransferCard
      logger={logger}
      activity={activity}
      params={params}
      locale={locale}
      isEnglishOnlyChatSupportAvailable={isEnglishOnlyChatSupportAvailable}
      cardText={(cardTextOverride || isRedirectLiveAgentEnabled) ? liveAgentCardText : undefined}
      onLiveAgentTransferClick={onLiveAgentTransferClick}
      onFeedbackButtonClick={onFeedbackButtonClick}
      isCardButtonDisabled={isCardButtonDisabled}
      isIntelligentRoutingEnabled={isIntelligentRoutingEnabled}
      isAIDegraded={isAIDegraded}
      disabled={disabled}
      isCaptchaEnabled={isCaptchaEnabled}
      renderConnectAction={renderConnectAction}
    />
  );
};

LiveAgentTransferCardRenderer.canRender = (activity: Activity, isFromUser: boolean) => {
  const activityValue = _.get(activity, 'value');
  if (!isFromUser && activity.type === 'message' && activityValue === 'ConnectToAgent') {
    return true;
  }

  if (activity.type === 'event') {
    if (activity.name === EVENT_NAME_ENGAGE_TROUBLESHOOTER && activityValue === 'RedirectToLiveAgent') {
      return true;
    }
    if (activity.name === EVENT_NAME_LIVE_AGENT_CHAT) {
      return true;
    }
  }

  return false;
};
